import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CollegeSavings = () => {
  // State variables for College Savings calculation
  const [tuition, setTuition] = useState(0); // Current tuition cost
  const [fees, setFees] = useState(0); // Current fees
  const [livingExpenses, setLivingExpenses] = useState(0); // Current living expenses
  const [inflationRate, setInflationRate] = useState(0); // Annual inflation rate
  const [fundGoal, setFundGoal] = useState(0); // Calculated college fund goal

  // Event handlers to update state values
  const handleTuitionChange = (e) =>
    setTuition(parseFloat(e.target.value) || 0);
  const handleFeesChange = (e) => setFees(parseFloat(e.target.value) || 0);
  const handleLivingExpensesChange = (e) =>
    setLivingExpenses(parseFloat(e.target.value) || 0);
  const handleInflationRateChange = (e) =>
    setInflationRate(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the college savings goal when any input changes
  useEffect(() => {
    if (tuition && fees && livingExpenses && inflationRate) {
      const totalCost =
        (tuition + fees + livingExpenses) * (1 + inflationRate / 100); // Adjusted cost for inflation
      setFundGoal(totalCost);
    } else {
      setFundGoal(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [tuition, fees, livingExpenses, inflationRate]);

  return (
    <Layout title="College Savings Calculator" path="college-savings">
      <section className="section-padding" id="college-savings-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* College Savings Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">College Savings Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Tuition Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="tuition" className="form-label">
                        Current Tuition (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Current Tuition:</strong> The amount you currently pay annually for tuition."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="tuition"
                        placeholder="Enter current tuition cost"
                        value={tuition || ""}
                        onChange={handleTuitionChange}
                      />
                    </div>

                    {/* Fees Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="fees" className="form-label">
                        Current Fees (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Current Fees:</strong> The amount you currently pay for additional school fees (e.g., administration, books)."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="fees"
                        placeholder="Enter current fees"
                        value={fees || ""}
                        onChange={handleFeesChange}
                      />
                    </div>

                    {/* Living Expenses Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="livingExpenses" className="form-label">
                        Current Living Expenses (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Current Living Expenses:</strong> The annual cost of housing, food, transportation, and other living expenses."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="livingExpenses"
                        placeholder="Enter living expenses"
                        value={livingExpenses || ""}
                        onChange={handleLivingExpensesChange}
                      />
                    </div>

                    {/* Inflation Rate Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="inflationRate" className="form-label">
                        Inflation Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Inflation Rate:</strong> The annual percentage increase in the cost of tuition, fees, and living expenses."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="inflationRate"
                        placeholder="Enter annual inflation rate"
                        value={inflationRate || ""}
                        onChange={handleInflationRateChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        College Fund Goal:{" "}
                        <span className="fw-bold ms-3">
                          {fundGoal === 0
                            ? "0.00"
                            : fundGoal.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the College Savings Calculator
                </h3>

                {/* What is a College Savings Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a College Savings Calculator?
                </h4>
                <p>
                  A **College Savings Calculator** helps parents and individuals
                  estimate the amount of savings they will need to cover college
                  expenses in the future, considering inflation and rising
                  tuition costs.
                </p>

                {/* Why Use a College Savings Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a College Savings Calculator?
                </h4>
                <p>
                  This calculator helps families prepare for the high costs of
                  education by estimating future tuition, fees, and living
                  expenses. It factors in inflation, providing a more accurate
                  picture of future savings needs.
                </p>

                {/* Benefits of Using a College Savings Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a College Savings Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps Parents Plan Long-Term Education Savings:
                    </strong>{" "}
                    Gives a clear estimate of how much needs to be saved over
                    the years for college.
                  </li>
                  <li>
                    <strong>
                      Adjusts for Inflation and Rising Tuition Costs:
                    </strong>{" "}
                    Factors in inflation to ensure that future education costs
                    are covered.
                  </li>
                  <li>
                    <strong>Provides Insight into Funding Needs:</strong> Helps
                    families understand how much they should aim to save for
                    their children's education.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a College Savings Calculator?
                </h4>
                <p>The College Savings Calculator is useful for:</p>
                <ul>
                  <li>
                    <strong>Parents:</strong> Helps parents plan for their
                    children’s future education costs, including tuition and
                    living expenses.
                  </li>
                  <li>
                    <strong>Personal Finance Advisors:</strong> Advisors can use
                    the tool to assist clients in planning for education
                    savings.
                  </li>
                  <li>
                    <strong>Education Planners:</strong> Helps families plan for
                    the high cost of education, accounting for inflation and
                    other rising expenses.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How does inflation affect college savings?
                </h4>
                <p>
                  Inflation increases the cost of goods and services over time,
                  including tuition and living expenses. Using an inflation rate
                  helps you estimate how much more expensive education might be
                  in the future.
                </p>
                <h4 className="mt-4 mb-3">
                  Why is it important to consider living expenses?
                </h4>
                <p>
                  College students typically incur living expenses in addition
                  to tuition and fees. It's important to estimate and include
                  these expenses to ensure a complete savings plan for all
                  aspects of college life.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CollegeSavings;
